import React, { useState, useRef, useEffect } from 'react';
import {
    Bell,
    X,
    Check,
    CheckCheck,
    ShoppingCart,
    CreditCard,
    AlertTriangle,
    Package,
    Users,
    Truck,
    Info,
    Wifi,
    WifiOff,
    ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useNotifications, Notification } from '../../contexts/NotificationContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { AlertCategory } from '../../lib/alertTypes';
import { cn } from '../../lib/utils';

// Category configuration
const CATEGORIES: { key: AlertCategory | 'all'; label: string; icon: React.ReactNode }[] = [
    { key: 'all', label: 'All', icon: <Bell className="w-3.5 h-3.5" /> },
    { key: 'orders', label: 'Orders', icon: <ShoppingCart className="w-3.5 h-3.5" /> },
    { key: 'payments', label: 'Payments', icon: <CreditCard className="w-3.5 h-3.5" /> },
    { key: 'breaches', label: 'Breaches', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
    { key: 'stock', label: 'Stock', icon: <Package className="w-3.5 h-3.5" /> },
    { key: 'status', label: 'Status', icon: <Users className="w-3.5 h-3.5" /> },
];

// Priority colors
const PRIORITY_COLORS = {
    critical: 'bg-red-500',
    high: 'bg-amber-500',
    normal: 'bg-blue-500',
    low: 'bg-slate-400',
};

// Priority indicator styles
const PRIORITY_RING = {
    critical: 'ring-2 ring-red-500 ring-offset-2',
    high: 'ring-2 ring-amber-500 ring-offset-1',
    normal: '',
    low: '',
};

// Format relative time
const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
};

// Get icon for notification type
const getTypeIcon = (type: Notification['type'], category?: AlertCategory) => {
    if (category === 'orders') return <ShoppingCart className="w-4 h-4" />;
    if (category === 'payments') return <CreditCard className="w-4 h-4" />;
    if (category === 'breaches') return <AlertTriangle className="w-4 h-4" />;
    if (category === 'stock') return <Package className="w-4 h-4" />;
    if (category === 'status') return <Truck className="w-4 h-4" />;

    switch (type) {
        case 'error': return <AlertTriangle className="w-4 h-4" />;
        case 'warning': return <AlertTriangle className="w-4 h-4" />;
        case 'success': return <Check className="w-4 h-4" />;
        default: return <Info className="w-4 h-4" />;
    }
};

interface AlertItemProps {
    notification: Notification;
    onMarkRead: (id: string) => void;
    onDismiss: (id: string) => void;
    onNavigate: (url: string) => void;
}

const AlertItem: React.FC<AlertItemProps> = ({
    notification,
    onMarkRead,
    onDismiss,
    onNavigate
}) => {
    return (
        <div
            className={cn(
                'group px-4 py-3 border-b border-border last:border-0 transition-colors',
                !notification.read && 'bg-primary/5',
                'hover:bg-accent/50'
            )}
        >
            <div className="flex items-start gap-3">
                {/* Priority indicator and icon */}
                <div className="relative flex-shrink-0 mt-0.5">
                    <div className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center',
                        notification.type === 'error' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                            notification.type === 'warning' ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' :
                                notification.type === 'success' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' :
                                    'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
                        PRIORITY_RING[notification.priority || 'normal']
                    )}>
                        {getTypeIcon(notification.type, notification.category || 'general')}
                    </div>
                    {/* Unread dot */}
                    {!notification.read && (
                        <span className={cn(
                            'absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card',
                            PRIORITY_COLORS[notification.priority || 'normal']
                        )} />
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <h4 className={cn(
                            'text-sm font-medium truncate',
                            !notification.read && 'text-foreground',
                            notification.read && 'text-muted-foreground'
                        )}>
                            {notification.title}
                        </h4>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                            {formatTimeAgo(notification.timestamp)}
                        </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notification.message}
                    </p>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {notification.action && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs px-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onNavigate(notification.action!.url);
                                }}
                            >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                View
                            </Button>
                        )}
                        {!notification.read && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs px-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onMarkRead(notification.id);
                                }}
                            >
                                <Check className="w-3 h-3 mr-1" />
                                Mark read
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs px-2 text-muted-foreground hover:text-destructive"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDismiss(notification.id);
                            }}
                        >
                            <X className="w-3 h-3" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function AlertCenter() {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<AlertCategory | 'all'>('all');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        removeNotification,
        getNotificationsByCategory,
        getCriticalAlerts,
    } = useNotifications();

    const { isConnected } = useWebSocket();

    const criticalAlerts = getCriticalAlerts();
    const hasCritical = criticalAlerts.length > 0;

    // Filter notifications by category
    const filteredNotifications = getNotificationsByCategory(selectedCategory);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNavigate = (url: string) => {
        setIsOpen(false);
        window.location.href = url;
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <Button
                variant="ghost"
                size="icon"
                className={cn(
                    'relative text-muted-foreground transition-colors',
                    hasCritical && 'animate-pulse'
                )}
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Notifications"
            >
                <Bell className={cn(
                    'h-5 w-5 transition-colors',
                    hasCritical && 'text-red-500'
                )} />

                {/* Badge */}
                {unreadCount > 0 && (
                    <Badge
                        className={cn(
                            'absolute -top-1 -right-1 px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold',
                            hasCritical ? 'bg-red-500 hover:bg-red-600' : 'bg-primary'
                        )}
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                )}
            </Button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-card rounded-lg shadow-xl border border-border z-50 overflow-hidden">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-border bg-muted/30">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-sm">Notifications</h3>
                                {/* Connection Status */}
                                <div className={cn(
                                    'flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
                                    isConnected
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                )}>
                                    {isConnected ? (
                                        <>
                                            <Wifi className="w-3 h-3" />
                                            Live
                                        </>
                                    ) : (
                                        <>
                                            <WifiOff className="w-3 h-3" />
                                            Offline
                                        </>
                                    )}
                                </div>
                            </div>

                            {unreadCount > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs px-2 gap-1"
                                    onClick={markAllAsRead}
                                >
                                    <CheckCheck className="w-3.5 h-3.5" />
                                    Mark all read
                                </Button>
                            )}
                        </div>

                        {/* Category Filter */}
                        <div className="flex items-center gap-1 mt-3 -mx-1 overflow-x-auto pb-1 scrollbar-hide">
                            {CATEGORIES.map((cat) => (
                                <Button
                                    key={cat.key}
                                    variant={selectedCategory === cat.key ? 'default' : 'ghost'}
                                    size="sm"
                                    className={cn(
                                        'h-7 text-xs px-2 gap-1 flex-shrink-0',
                                        selectedCategory === cat.key && 'shadow-sm'
                                    )}
                                    onClick={() => setSelectedCategory(cat.key)}
                                >
                                    {cat.icon}
                                    {cat.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Notification List */}
                    <div className="max-h-[60vh] overflow-y-auto">
                        {filteredNotifications.length === 0 ? (
                            <div className="px-4 py-12 text-center">
                                <Bell className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                                <p className="text-sm text-muted-foreground">
                                    {selectedCategory === 'all'
                                        ? 'No notifications yet'
                                        : `No ${selectedCategory} notifications`}
                                </p>
                                <p className="text-xs text-muted-foreground/70 mt-1">
                                    {isConnected
                                        ? 'New alerts will appear here in real-time'
                                        : 'Connect to receive real-time alerts'}
                                </p>
                            </div>
                        ) : (
                            filteredNotifications.map((notification) => (
                                <AlertItem
                                    key={notification.id}
                                    notification={notification}
                                    onMarkRead={markAsRead}
                                    onDismiss={removeNotification}
                                    onNavigate={handleNavigate}
                                />
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    {filteredNotifications.length > 0 && (
                        <div className="px-4 py-2 border-t border-border bg-muted/30">
                            <p className="text-xs text-muted-foreground text-center">
                                Showing {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
